import * as React from 'react';
import { ControlGroup, Stack, Box, Link, Text, Button } from '@withjoy/joykit';
import { StackWrapper, Subtitle } from './styles';
import { PreviewProperty } from '@routes/event/guest/Template/services/previewListener/types';
import { LocationPicker, DatePicker } from './components';
import { SubmitValue } from './components/DatePicker';
import { LocationType, InputField, EventDateType, handleLocationInputType, onLocationSelectType } from '../../types';
import { useAnalytics, Analytics } from '@withjoy/web-core';
import { addMonths } from '../../util';
import { trimStart } from '../../../../../../utils/strings';

interface FormProps {
  handleOnInput: (field: PreviewProperty, value: string, formError: boolean) => void;
  handleLocationInput: handleLocationInputType;
  handleDate: (value: Date) => void;
  handleClearForm: () => void;
  handleTimezoneChange: (method: string) => void;
  handleNotApplicable: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputErrors: (
    ownerFirstName: InputField,
    ownerLastName: InputField,
    partnerFirstName: InputField,
    partnerLastName: InputField,
    location: LocationType,
    analytics: Analytics
  ) => void;
  onSubmit: () => void;
  ownerFirstName: InputField;
  ownerLastName: InputField;
  partnerFirstName: InputField;
  partnerLastName: InputField;
  eventDate: EventDateType;
  location: LocationType;
  buttonText: string;
  ctaText: {
    title: string;
    subtitle: string;
  };
  popupOpen?: boolean;
  inPopup?: boolean;
}
interface FormComponentProps extends FormProps {
  analytics: Analytics;
}

function chunkArray(arr: JSX.Element[], chunkSize: number) {
  var results = [];

  while (arr.length) {
    results.push(arr.splice(0, chunkSize));
  }

  return results;
}
class CreateEventFormComponent extends React.Component<FormComponentProps, {}> {
  state = {
    cantSubmit: false
  };

  componentWillUnmount() {
    this.props.analytics.page.pageExit();
  }

  handleFormInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = trimStart(event.currentTarget.value);
    const field = event.currentTarget.dataset.input;
    this.props.handleOnInput(field as PreviewProperty, value, this.state.cantSubmit);
  };

  handleDateInput = (submitDate: SubmitValue) => {
    const { year, month, date: day } = submitDate;
    const date = new Date(year, month, day);
    this.props.handleDate(date);
  };

  handleLocationInput: onLocationSelectType = (value, lat, lng, placeId, tzid) => {
    this.props.handleLocationInput(value, lat, lng, placeId, tzid, this.state.cantSubmit);
  };

  canSubmit = () => {
    const { ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, location } = this.props;
    return (
      ownerFirstName.value !== '' &&
      ownerLastName.value !== '' &&
      partnerFirstName.value !== '' &&
      partnerLastName.value !== '' &&
      location.value !== ''
    );
  };

  handleClickForAnalytics = (event: React.FocusEvent<HTMLElement>) => {
    const field = event.target.dataset.input || 'missingDataInput'; // this should never happen
    const action = `${field.charAt(0).toUpperCase()}${field.slice(1)}`;
    this.props.analytics.track({ action, actionType: 'click' });
  };

  getDateForTracking = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

  getDateLabelForTracking = (date: Date) => {
    if (date.toDateString() === new Date().toDateString()) {
      return 'today';
    } else if (date.toDateString() === addMonths(new Date(), 9).toDateString()) {
      return 'default';
    } else {
      return 'custom';
    }
  };

  handleFormSubmit: React.FormEventHandler = event => {
    event.preventDefault();
    if (!this.canSubmit()) {
      const { ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, location, analytics } = this.props;
      this.props.handleInputErrors(
        ownerFirstName,
        ownerLastName,
        partnerFirstName,
        partnerLastName,
        location,
        analytics
      );
    } else {
      const { location, eventDate } = this.props;
      const formattedDate = this.getDateForTracking(eventDate.value);
      const dateLabel = this.getDateLabelForTracking(eventDate.value);

      this.props.analytics.track({ action: 'TimezoneRegistered', extraInfo: { method: location.tzMethod } });
      this.props.analytics.track({ action: 'DateSet', label: dateLabel, extraInfo: { eventDate: formattedDate } });
      this.props.analytics.track({ action: 'SubmitDetails', actionType: 'click', label: 'continue' });

      this.props.onSubmit();
    }
  };

  getFieldInputs = () => {
    const { ownerFirstName, ownerLastName, partnerFirstName, partnerLastName } = this.props;
    const inputArr = [ownerFirstName, ownerLastName, partnerFirstName, partnerLastName].map(field => {
      return (
        <div key={field.name} style={{ flexGrow: 1, flexShrink: 1, flexBasis: 'auto' }}>
          <ControlGroup
            onFocus={this.handleClickForAnalytics}
            label={field.label}
            error={field.error ? field.errorText : field.error}
          >
            <ControlGroup.Input
              error={field.error}
              name={field.name}
              placeholder={field.placeholder}
              label={field.label}
              data-input={field.name}
              onChange={this.handleFormInput}
              value={field.value}
            />
          </ControlGroup>
        </div>
      );
    });
    const rows = chunkArray(inputArr, 2).map((rowContent, index) => {
      return (
        <Stack key={`row-${index}`} vertical={false} spacing="sm" stackVerticalOnMobile={true}>
          {rowContent}
        </Stack>
      );
    });

    return <Stack spacing="md">{rows}</Stack>;
  };

  render() {
    const {
      eventDate,
      location,
      buttonText,
      ctaText,
      analytics,
      inPopup,
      popupOpen,
      handleTimezoneChange
    } = this.props;
    if (inPopup && popupOpen) {
      this.props.analytics.page.pageEnter();
    }
    return (
      <StackWrapper spacing="md">
        <StackWrapper spacing="xs">
          <Text textAlign="center" component="h1" variant="page">
            {ctaText.title}
          </Text>
          <Subtitle component="p" variant="body">
            {ctaText.subtitle}
          </Subtitle>
        </StackWrapper>
        <form onSubmit={this.handleFormSubmit}>
          <Stack spacing="sm">
            {this.getFieldInputs()}
            <DatePicker
              date={eventDate.value}
              changed={eventDate.changed}
              onChange={this.handleDateInput}
              disabled={eventDate.notApplicable}
              analytics={analytics}
            />
            {/* <div style={{ justifyContent: 'flex-start', marginTop: '8px' }} className={styles.inputRow}>
              <Checkbox data-field="eventDate" onChange={handleNotApplicable} checked={eventDate.notApplicable} />
              <span style={{ marginLeft: '8px' }}>I don't have a date yet!</span>
            </div> */}{' '}
            {/* <LocationSearch
              location={location}
              handleAddressChange={handleAddressChange}
              handleLocationChange={this.handleLocationInput}
              disabled={location.notApplicable}
              required={true}
              analytics={analytics}
            /> */}
            <LocationPicker
              value={location.value}
              error={location.error}
              onSelected={this.handleLocationInput}
              analytics={analytics}
              handleTimezoneChange={handleTimezoneChange}
            />
            {/* <div style={{ justifyContent: 'flex-start', marginTop: '8px' }} className={styles.inputRow}>
              <Checkbox data-field="location" onChange={handleNotApplicable} checked={location.notApplicable} />
              <span style={{ marginLeft: '8px' }}>I don't have a location yet!</span>
            </div> */}
            <Box marginX={{ xs: '1rem', sm: '4rem', lg: '6rem' }}>
              <Stack spacing="sm">
                <Button type="submit">{buttonText}</Button>
                <Text textAlign="center" component="p" variant="body">
                  By clicking the button above you agree to Joy's{' '}
                  <Link href="https://withjoy.com/EULA/" target="_blank">
                    Terms of Service Agreement
                  </Link>
                </Text>
              </Stack>
            </Box>
          </Stack>
        </form>
      </StackWrapper>
    );
  }
}

export const CreateEventForm: React.SFC<FormProps> = ({ inPopup, ...props }) => {
  const analytics = useAnalytics({
    pagePrefix: 'marketing',
    category: 'CreateWedding',
    page: 'weddingDetailsForm',
    deferPageTrack: inPopup ? true : false,
    extraPageInfo: { label: inPopup ? 'modal' : 'fullPage' }
  });

  return <CreateEventFormComponent inPopup={inPopup} analytics={analytics} {...props} />;
};
